// /src/index.js
import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { createStore, applyMiddleware } from "redux";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "./index.css";

import * as serviceWorker from "./serviceWorker";
// import ReduxThunk from "redux-thunk";
import { thunk } from "redux-thunk";

import rootReducer from "./reducers/rootReducer";
import { TabsRouter } from "./routes";
import CacheBuster from "./cacheBuster";
import ErrorBoundary from "./components/ErrorBoundary";
import { reportClientError } from "./lib/clientLog";
import { bootstrapAuth } from "./lib/authMode";

import "bootstrap/dist/css/bootstrap.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});
function configureStore(initialState = {}) {
  //const reduxMiddleware = applyMiddleware(ReduxThunk, createLogger());
  const reduxMiddleware = applyMiddleware(thunk);
  return createStore(rootReducer, initialState, reduxMiddleware);
}
let configuredStore = configureStore();

const container = document.getElementById("root");
const root = createRoot(container);
const render = () =>
  root.render(
    <ErrorBoundary>
      <Provider store={configuredStore}>
        <QueryClientProvider client={queryClient}>
          <CacheBuster>
            {({ loading, isLatestVersion, refreshCacheAndReload }) => {
              if (loading) return null;
              if (!loading && !isLatestVersion) {
                refreshCacheAndReload();
              }
              return <TabsRouter store={configuredStore} />;
            }}
          </CacheBuster>
        </QueryClientProvider>
      </Provider>
    </ErrorBoundary>,
  );
// Learn the login mode (and, in msal mode, the server session) before the
// first render, since App decides signed-in vs. login screen synchronously.
bootstrapAuth().then(render, render);
serviceWorker.unregister();

// Diagnostic: log all unhandled promise rejections with their source promise
window.addEventListener("unhandledrejection", (event) => {
  // Axios errors carry request headers (ClientToken); never print them in prod.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error("[Unhandled Rejection] reason:", event.reason, "\npromise:", event.promise);
    // eslint-disable-next-line no-console
    console.trace("[Unhandled Rejection] current stack trace (where the listener fired, not the rejection origin)");
  }
  // The PowerBI SDK rejects internal promises with `undefined` (not an Error) when an embedded
  // element is reset mid-flight during navigation (e.g. Embed.load, Embed.frontLoadSendConfig).
  // These are expected SDK behaviour and are not application bugs.  Calling preventDefault()
  // suppresses the webpack dev-server overlay for these harmless rejections while keeping the
  // console.error above so they remain visible in the browser console.
  if (event.reason === undefined) {
    event.preventDefault();
    return; // expected PBI SDK noise — don't report
  }
  reportClientError({
    type: "unhandledrejection",
    message:
      (event.reason && (event.reason.message || event.reason.toString())) ||
      "Unhandled promise rejection",
    detail: {
      stack: event.reason && event.reason.stack,
      // Axios errors: identify the failing endpoint (path only, no query string).
      request: event.reason?.isAxiosError
        ? `${event.reason.config?.method?.toUpperCase()} ${String(event.reason.config?.url || "").split("?")[0]} -> ${
          event.reason.response?.status
        }`
        : undefined,
    },
  });
});

// Report uncaught script errors (skip resource-load errors from img/script/link).
window.addEventListener("error", (event) => {
  if (event.target && event.target !== window) return;
  reportClientError({
    type: "window-error",
    message: event.message || "Uncaught error",
    detail: {
      source: event.filename,
      line: event.lineno,
      col: event.colno,
      stack: event.error && event.error.stack,
    },
  });
});
